import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { IActiveUser, IAuth0Profile, IRawAuth0Profile } from '../models/active-user';

@Injectable({
  providedIn: `root`,
})
export class ActiveUserService {
  private authService: AuthService = inject(AuthService);

  private normaliseRawAuth0Profile(rawAuth0Profile: IRawAuth0Profile, customClaimNamespace: string): IAuth0Profile {
    const prefix = `${customClaimNamespace}/`;
    return Object.entries(rawAuth0Profile).reduce((acc, [ key, value ]) => {
      if (key.startsWith(prefix)) {
        key = key.substring(prefix.length);
      }
      acc[ key ] = value;
      return acc;
    }, {}) as IAuth0Profile;
  }

  private auth0ProfileHasScope(auth0Profile: IAuth0Profile, scope: string): boolean {
    return auth0Profile.app_metadata?.scope?.includes(scope) ?? false;
  }

  private convertToActiveUserObject(auth0Profile: IAuth0Profile): IActiveUser | undefined {
    if (!auth0Profile) {
      return undefined;
    }

    const canEditAnyUserScopes = this.auth0ProfileHasScope(auth0Profile, `users:read`) && this.auth0ProfileHasScope(auth0Profile, `users:scopes:write`);
    const canEditOwnScopes = canEditAnyUserScopes || this.auth0ProfileHasScope(auth0Profile, `users:scopes:write:self`);
    const canEditAnyUserOrganisations = this.auth0ProfileHasScope(auth0Profile, `organizations:write-all`);

    return {
      ...auth0Profile,
      userid: auth0Profile.sub,
      displayName: auth0Profile.name?.trim() || auth0Profile.nickname?.trim() || auth0Profile.email?.trim() || `User`,
      canEditOwnScopes,
      canEditAnyUserScopes,
      canEditAnyUserOrganisations,
    };
  }

  public getActiveUser$(): Observable<IActiveUser | undefined> {
    return this.authService.user$
      .pipe(
        map(rawAuth0Profile => this.normaliseRawAuth0Profile(rawAuth0Profile, environment.auth0.claimsNamespacePrefix)),
        map(normalizedAuth0Profile => this.convertToActiveUserObject(normalizedAuth0Profile)),
      );
  }
}
